// @flow
import { call, put, race, select, take, delay } from 'redux-saga/effects';

import HandleErrorService from '../../../lib/handleError';
import I18n from '../../../lib/i18n';

import { loginCall } from '../../Authentication/sagas';
import { selectSubscription } from '../../Dashboard';
import { fetchAll, isSubscriptionVisible, subscriptionByOuidSelector } from '../../Subscriptions';
import Toast from '../../../lib/toastService';
import { setError, setLoading } from '../../LoadingStatus/actions';
import { loginSelector } from '../Login';
import { orderUuidSelector, setOrderStateForUuid } from '../Order';
import { portabilityCharacteristicsSelector } from '../PhoneNumber';
import { tripica } from '../../../api';
import { trackAnalyticsEvent } from '../../../lib/analytics';
import { customerExternalIdSelector } from '../../Customer/selectors';
import { isLoggedInSelector } from '../../Authentication';
import { triggerPaymentProductOrderPolling } from '../../PollingOrder';
import WizzeeLog from '../../../lib/wizzeeLog';

export function* pollForActivation(): Generator<*, *, *> {
  const externalId = yield select(customerExternalIdSelector);
  try {
    yield put(setLoading('redirectAfterActivating', true));
    yield put(setError('redirectAfterActivating', null));

    const uuid = yield select(orderUuidSelector);
    const { timeout } = yield race({
      orderStatusCheck: call(checkLogin),
      timeout: delay(35000),
    });

    let productOrder;
    if (!timeout) {
      productOrder = yield call(triggerPaymentProductOrderPolling, 'ACTIVATION', uuid);
    }

    switch (productOrder?.state) {
      case 'COMPLETED':
        trackAnalyticsEvent('activation_success', { externalId });
        break;
      case 'FAILED':
      case undefined:
      case null:
        yield put(setError('redirectAfterActivating', { type: 'FAILED', title: I18n.t('error.orderFailed') }));
        break;
      case 'IN_PROGRESS':
      default:
        trackAnalyticsEvent('activation_in_progress', { externalId });
        const portabilityCharacteristics = yield select(portabilityCharacteristicsSelector);
        if (portabilityCharacteristics) break;
        yield put(
          setError('redirectAfterActivating', {
            type: 'IN_PROGRESS',
            title: I18n.t('SignUp.Congrats.activationInProgress'),
          })
        );
        break;
    }

    yield put(setOrderStateForUuid(null, null));
  } catch (e) {
    WizzeeLog.warn('[saga] pollForActivation', e);
    yield put(setError('redirectAfterActivating', { title: HandleErrorService.getErrorMessage(e) }));
    HandleErrorService.showToastError(e);
  } finally {
    yield put(setLoading('redirectAfterActivating', false));
  }
}

export function* checkLogin(): Generator<*, *, *> {
  const loginInfos = yield select(loginSelector);
  // Use a mutable flag so the return value reflects the state AFTER the loop,
  // not just the snapshot taken before it.
  let loggedIn = yield select(isLoggedInSelector);
  if (!loggedIn) {
    while (true) {
      try {
        // skipOtpTokenCapture prevents a PIN_REQUIRED 401 from overwriting
        // tripicaBearerToken with a temp JWT. Background re-login cannot drive
        // the OTP flow (no UI), so capturing the temp token here would corrupt
        // subsequent calls such as fetchCustomerSaga → 403.
        const loginResult = yield call(loginCall, loginInfos, {
          bypassUnauthorizedRequestCheck: true,
          skipOtpTokenCapture: true,
        });

        WizzeeLog.log('>>> checkLogin:: loginResult - ', loginResult);

        if (loginResult === 'PIN_REQUIRED') {
          // OTP is required and cannot be completed in the background — abort
          // the re-login loop immediately. tripicaBearerToken is preserved as-is
          // (e.g. the JWT returned by tripica.register) thanks to skipOtpTokenCapture.
          break;
        }

        if (yield select(isLoggedInSelector)) {
          loggedIn = true;
          break;
        }
      } catch (e) {
        WizzeeLog.warn('[saga] checkLogin', e);
      }

      yield delay(2000);
    }
  }
  return loggedIn;
}

export function* orderStatusCheck(): Generator<*, *, *> {
  yield delay(2000);
  const isLoggedIn = yield call(checkLogin);
  if (!isLoggedIn) {
    // Cannot proceed unauthenticated: OTP required or login failed.
    // Calling pollOrderForStatus without a valid token triggers a 403 loop
    // (UNAUTHORIZED_REQUEST → resetToWelcomeScreen on every retry).
    return;
  }
  const uuid = yield select(orderUuidSelector);
  const { success, targetOuid } = yield call(pollOrderForStatus, uuid);

  if (success && targetOuid) {
    yield call(selectOrderSubscription, targetOuid);
  }
}

export function* selectOrderSubscription(targetOuid: string): Generator<*, *, *> {
  yield put(fetchAll());
  const res = yield race({
    success: take('SUBSCRIPTION_FETCH_ALL_SUCCESS'),
    failure: take('SUBSCRIPTION_FETCH_ALL_FAILURE'),
  });
  if (res.failure) {
    return false;
  }

  const subscription = yield select(subscriptionByOuidSelector, targetOuid);

  if (isSubscriptionVisible(subscription)) {
    yield put(selectSubscription(subscription));
  }
}

export function* checkOrderForStatus(uuid: string): Generator<*, *, *> {
  const productOrder = yield call([tripica, 'getProductOrderBySandBoxUuid'], uuid);
  const productOrderState = {
    state: productOrder && productOrder.state,
    targetOuid: undefined,
  };
  yield put(setOrderStateForUuid(productOrderState.state, uuid));

  if (productOrderState.state === 'COMPLETED') {
    const orderItems = productOrder.orderItems;

    const item = orderItems.find(
      orderItem =>
        orderItem.product &&
        orderItem.product.productRelationships &&
        orderItem.product.productRelationships.find(relationship => relationship.type === 'PARENT')
    );

    const itemRelationship =
      item && item.product.productRelationships.find(relationship => relationship.type === 'PARENT');

    return {
      ...productOrderState,
      targetOuid: itemRelationship && itemRelationship.targetProductOuid,
    };
  }
  return productOrderState;
}

export function* pollOrderForStatus(uuid: string): Generator<*, *, *> {
  const statesToExitPolling = ['COMPLETED', 'FAILED'];
  while (true) {
    try {
      const { state, targetOuid } = yield call(checkOrderForStatus, uuid);

      if (statesToExitPolling.includes(state)) {
        return { success: state === 'COMPLETED', targetOuid };
      }
    } catch (e) {
      Toast.showMessage(I18n.t('error.serverError'));
      WizzeeLog.warn('[saga] pollOrderForStatus', e);
    }
    yield delay(2000);
  }
}
